import React, { FunctionComponent, CSSProperties } from 'react';
import classNames from 'classnames';
import type { Favorite, LikeHistory, Recruitment, Scout } from '../../../types/personShow';
import type { ScoutModalHistoryKey } from './types';
import { ScoutSendForm } from '../scoutSendForm';
import { ScoutLikePanel } from '../scoutLikePanel';

type Props = {
  icon: string;
  personId: number;
  candidateSummary?: React.ReactNode;
  onLike?: (event: React.MouseEvent<HTMLElement>) => void;
  isLiked?: boolean;
  isBookmarked: boolean;
  isAlreadyConnected: boolean;
  messageBoxId?: number | null;
  isAllowedToScout: boolean;
  style: CSSProperties;
  scoutableRecruitments: Recruitment[];
  favorites: Favorite[];
  likes: LikeHistory[];
  scouts: Scout[];
  openHistories: ScoutModalHistoryKey[];
  targetRecruitment: number | null;
  selectedRecruitment?: Recruitment;
  visibleRecruitments: Recruitment[];
  isRecruitmentSelectOpen: boolean;
  recruitmentKeyword: string;
  messageBody: string;
  recruitmentSelectRef: React.RefObject<HTMLDivElement>;
  isSubmitting: boolean;
  handleModalClose: () => void;
  handleSubmit: () => void;
  selectRecruitment: (recruitmentId: number) => void;
  toggleRecruitmentSelect: () => void;
  changeRecruitmentKeyword: (keyword: string) => void;
  changeMessageBody: (message: string) => void;
  toggleHistory: (target: ScoutModalHistoryKey) => void;
  handleCreateBookmark?: () => void;
  handleDeleteBookmark?: () => void;
  outOfLike?: boolean;
  outOfScout?: boolean;
};

export const Presentation: FunctionComponent<Props> = ({
  icon,
  personId,
  candidateSummary,
  onLike,
  isLiked,
  isBookmarked,
  isAlreadyConnected,
  messageBoxId,
  isAllowedToScout,
  style,
  scoutableRecruitments,
  favorites,
  likes,
  scouts,
  openHistories,
  targetRecruitment,
  selectedRecruitment,
  visibleRecruitments,
  isRecruitmentSelectOpen,
  recruitmentKeyword,
  messageBody,
  recruitmentSelectRef,
  isSubmitting,
  handleModalClose,
  handleSubmit,
  selectRecruitment,
  toggleRecruitmentSelect,
  changeRecruitmentKeyword,
  changeMessageBody,
  toggleHistory,
  handleCreateBookmark,
  handleDeleteBookmark,
  outOfLike,
  outOfScout,
}) => {
  const scoutButtonDisabled = Boolean(
    isSubmitting || !targetRecruitment || !messageBody.trim() || outOfScout
  );
  const historySections = [
    { key: 'favorites' as const, title: '求人保存履歴', items: favorites },
    { key: 'likes' as const, title: '気になる履歴', items: likes },
    { key: 'scouts' as const, title: 'スカウト履歴', items: scouts },
  ];
  return (
    <div className="scouts-edit-modal js-present-id" style={style}>
      <div className="scouts-edit-modal-overlay js-modal-close" onClick={handleModalClose}></div>
      <div className="scouts-edit-modal-container">
        <div className="scouts-edit-modal-detail">
          {candidateSummary || (
            <div className="scouts-edit-modal-heading">
              <img src={icon} alt="プロフィール写真" className="scouts-edit-modal-author-logo-img" />
            </div>
          )}
          <div className="scouts-edit-modal-bottom-panel">
            <button
              type="button"
              className={classNames('scouts-edit-modal-bottom-panel__bookmark', { 'is-bookmarked': isBookmarked })}
              onClick={isBookmarked ? handleDeleteBookmark : handleCreateBookmark}
              disabled={!handleCreateBookmark || !handleDeleteBookmark}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {isBookmarked ? 'bookmark' : 'bookmark_border'}
              </span>
              {isBookmarked ? 'ブックマークを解除する' : 'ブックマークに登録する'}
            </button>
            {isAlreadyConnected && messageBoxId ? (
              <a
                className="scouts-edit-modal-bottom-panel__message"
                href={`/management/messages/${messageBoxId}`}
                target="_blank"
                rel="noreferrer"
              >
                メッセージする
              </a>
            ) : (
              <button type="button" className="scouts-edit-modal-bottom-panel__message" disabled>
                メッセージする
              </button>
            )}
          </div>
        </div>
        <div className="scouts-edit-modal-form">
          <ScoutSendForm
            isAllowedToScout={isAllowedToScout}
            hasRecruitments={scoutableRecruitments.length > 0}
            selectedRecruitment={selectedRecruitment}
            visibleRecruitments={visibleRecruitments}
            targetRecruitment={targetRecruitment}
            isRecruitmentSelectOpen={isRecruitmentSelectOpen}
            recruitmentKeyword={recruitmentKeyword}
            scoutButtonDisabled={scoutButtonDisabled}
            recruitmentSelectRef={recruitmentSelectRef}
            messageBody={messageBody}
            toggleRecruitmentSelect={toggleRecruitmentSelect}
            changeRecruitmentKeyword={changeRecruitmentKeyword}
            selectRecruitment={selectRecruitment}
            changeMessageBody={changeMessageBody}
            handleSubmit={handleSubmit}
          />
          {onLike && (
            <ScoutLikePanel
              personId={personId}
              isLiked={Boolean(isLiked)}
              isOutOfLike={Boolean(outOfLike)}
              onLike={onLike}
            />
          )}
          <section className="scouts-edit-modal-histories">
            <h2>履歴</h2>
            {historySections.map(section => (
              <div className="scouts-edit-modal-history" key={section.key}>
                <button type="button" onClick={() => toggleHistory(section.key)}>
                  {section.title}
                  <svg className={classNames('scouts-edit-modal-history__arrow', { 'is-open': openHistories.includes(section.key) })} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="267.69 -596.31 424.62 235.62" width="10px" fill="#19190F"><path d="M480-360.69 267.69-573 291-596.31l189 189 189-189L692.31-573 480-360.69Z"/></svg>
                </button>
                {openHistories.includes(section.key) && (
                  <div className="scouts-edit-modal-history__list">
                    {section.items.length > 0 ? (
                      section.items.map(item => (
                        <div
                          className="scouts-edit-modal-history__item"
                          key={`${section.key}-${'recruitmentId' in item ? item.recruitmentId : item.id}`}
                        >
                          <time>{item.createdAt}</time>
                          <p>{item.title}</p>
                        </div>
                      ))
                    ) : (
                      <p className="scouts-edit-modal-history__empty">履歴はありません</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  )
};
